import React, { useEffect, useRef } from 'react';
import useViewport from '../hooks/useViewport';
import { cn } from './cn';
import ScrollStrip from './ScrollStrip';
import Tooltip from './Tooltip';

const SERIES_TONES = { sky: 'bg-bun-data-sky', ink: 'bg-bun-ink', success: 'bg-bun-success', danger: 'bg-bun-danger' };

/** Internal grouped plot for ColumnChart. Feature callers supply values and series identities. */
export default function GroupedColumnChart({ items, series, selectedId, onSelect, formatValue = String, referenceValue, referenceLabel, 'aria-label': ariaLabel }) {
    const { isMobile } = useViewport();
    const barHeight = isMobile ? 100 : 120;
    const scrollRef = useRef(null);
    const maximum = Math.max(1, Number.isFinite(referenceValue) ? Math.abs(referenceValue) : 0, ...items.flatMap(item => series.map(entry => Math.abs(item.values[entry.id]))));
    const heightFor = value => Math.abs(value) / maximum * barHeight;
    const Component = onSelect ? 'button' : 'div';

    useEffect(() => {
        const scroller = scrollRef.current;
        const active = scroller?.querySelector('[aria-pressed="true"]');
        if (!active) return undefined;
        const alignActive = () => {
            if (scroller.scrollWidth <= scroller.clientWidth) return;
            scroller.scrollLeft += active.getBoundingClientRect().right - scroller.getBoundingClientRect().right;
        };
        alignActive();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(alignActive);
        observer.observe(scroller);
        return () => observer.disconnect();
    }, [selectedId, items.length]);

    return (
        <ScrollStrip ref={scrollRef} className="pb-1" aria-label={ariaLabel} role="region">
            <div className={cn('relative isolate flex w-max min-w-full items-end gap-2 sm:w-auto sm:gap-3', items.length > 6 && 'sm:min-w-[calc(200%+8px)] md:min-w-0')} style={{ height: barHeight + 38 }}>
                {Number.isFinite(referenceValue) && (
                    <span className="pointer-events-none absolute inset-x-0 z-10 border-t border-dashed border-bun-stone"
                        style={{ bottom: 28 + heightFor(referenceValue) }} title={referenceLabel} aria-hidden="true" />
                )}
                {items.map(item => {
                    const active = item.id === selectedId;
                    const valuesLabel = series.map(entry => `${entry.label.toLowerCase()} ${formatValue(item.values[entry.id])}`).join(', ');
                    return (
                        <Tooltip key={item.id} portal multiline width="max-content" anchorClassName="w-16 min-w-0 shrink-0 sm:w-auto sm:flex-1"
                            label={<><span className="block font-semibold">{item.label}</span>{series.map(entry => <span key={entry.id} className="mt-1 block">{entry.label}: {formatValue(item.values[entry.id])}</span>)}</>}>
                            <Component type={onSelect ? 'button' : undefined} onClick={onSelect ? () => onSelect(item) : undefined}
                                role={onSelect ? undefined : 'img'} tabIndex={onSelect ? undefined : 0}
                                aria-label={`${item.label}: ${valuesLabel}`} aria-pressed={onSelect ? active : undefined}
                                className={cn('group relative flex w-full min-w-0 flex-1 flex-col justify-end gap-2 rounded-bun px-1 py-2 text-center font-instrument focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bun-blue',
                                    onSelect && 'cursor-pointer hover:bg-bun-mist-deep',
                                    active && 'bg-bun-mist-deep')}>
                                <span className="flex w-full items-end justify-center gap-1" style={{ height: barHeight + 2 }}>
                                    {series.map(entry => <span key={entry.id} data-series={entry.id} data-column-value={item.values[entry.id]}
                                        className={cn('block min-w-0 max-w-5 flex-1 rounded-bun-sm transition-colors sm:max-w-12', SERIES_TONES[entry.tone] || SERIES_TONES.ink)}
                                        style={{ height: heightFor(item.values[entry.id]) }} />)}
                                </span>
                                <span className={cn('text-bun-meta leading-none', active ? 'font-semibold text-bun-text' : 'font-book text-bun-stone')}>{item.axisLabel || item.label}</span>
                            </Component>
                        </Tooltip>
                    );
                })}
            </div>
        </ScrollStrip>
    );
}
